import { useState } from "react";
import api, { apiErrorMessage } from "@/lib/api";
import { toast } from "sonner";
import { Upload, FileSpreadsheet, X, CheckCircle2, AlertTriangle, Download } from "lucide-react";

/**
 * Reusable CSV import dialog.
 *
 * Props:
 *  - endpoint: string (e.g. "/import/products")
 *  - title: string
 *  - headers: array of strings - column names expected in CSV
 *  - sample: array of arrays - sample rows (string values)
 *  - templateName: string - filename for the CSV template download
 *  - onClose: () => void
 *  - onDone: () => void  (called after success to allow refresh)
 */
export default function CsvImportModal({ endpoint, title, headers, sample, templateName, onClose, onDone }) {
  const [file, setFile] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  const downloadTemplate = () => {
    const rows = [headers, ...sample];
    const csv = rows.map((r) => r.map((cell) => {
      const s = String(cell ?? "");
      return /[",;\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    }).join(",")).join("\n");
    const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = templateName;
    a.click();
    URL.revokeObjectURL(url);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!file) return;
    setSubmitting(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const { data } = await api.post(endpoint, formData, { headers: { "Content-Type": "multipart/form-data" } });
      setResult(data);
      const successCount = (data.created || 0) + (data.updated || 0);
      if (successCount > 0) {
        toast.success(`Zaimportowano ${successCount} pozycji (${data.created} nowych, ${data.updated} zaktualizowanych)`);
      }
      if (data.errors && data.errors.length > 0) {
        toast.error(`${data.errors.length} błędów – zobacz szczegóły poniżej`);
      }
      onDone && onDone();
    } catch (err) {
      toast.error(apiErrorMessage(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm" data-testid="csv-import-modal">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
          <div className="flex items-center gap-2">
            <FileSpreadsheet className="w-5 h-5 text-[#FF6B1A]" />
            <h3 className="font-bold text-slate-900">{title}</h3>
          </div>
          <button onClick={onClose} data-testid="csv-close" className="text-slate-400 hover:text-slate-700"><X className="w-5 h-5" /></button>
        </div>

        <div className="p-6 space-y-4">
          <div className="rounded-md bg-slate-50 border border-slate-200 p-4">
            <p className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-2">Wymagane kolumny CSV</p>
            <div className="flex flex-wrap gap-1.5">
              {headers.map((h) => (
                <span key={h} className="font-mono text-xs px-2 py-0.5 rounded bg-white border border-slate-300 text-slate-700">{h}</span>
              ))}
            </div>
            <button onClick={downloadTemplate} data-testid="csv-download-template"
              className="mt-3 inline-flex items-center gap-1.5 text-xs font-semibold text-[#FF6B1A] hover:text-[#E65A10]">
              <Download className="w-3.5 h-3.5" /> Pobierz szablon CSV
            </button>
            <p className="mt-2 text-xs text-slate-500">Akceptujemy separator <code className="font-mono">,</code> lub <code className="font-mono">;</code> (Excel PL). Kodowanie UTF-8 (z BOM) lub Windows-1250.</p>
          </div>

          {!result && (
            <form onSubmit={submit} className="space-y-3">
              <label className="block">
                <span className="block text-xs font-semibold uppercase tracking-wide text-slate-500 mb-1.5">Plik CSV *</span>
                <input
                  type="file"
                  accept=".csv,text/csv"
                  onChange={(e) => setFile(e.target.files[0])}
                  required
                  data-testid="csv-file-input"
                  className="block w-full text-sm text-slate-700 file:mr-3 file:py-2 file:px-4 file:rounded-md file:border-0 file:bg-[#FF6B1A] file:text-white file:font-semibold hover:file:bg-[#E65A10] file:cursor-pointer"
                />
              </label>
              <div className="flex justify-end gap-2 pt-2">
                <button type="button" onClick={onClose} className="px-4 py-2 rounded-md border border-slate-300 text-sm font-semibold text-slate-700 hover:bg-slate-50">Anuluj</button>
                <button type="submit" disabled={!file || submitting} data-testid="csv-submit"
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-[#FF6B1A] hover:bg-[#E65A10] disabled:opacity-60 text-white text-sm font-semibold">
                  <Upload className="w-4 h-4" /> {submitting ? "Importowanie…" : "Importuj"}
                </button>
              </div>
            </form>
          )}

          {result && (
            <div className="space-y-3" data-testid="csv-result">
              <div className="grid grid-cols-3 gap-2">
                <div className="rounded-md bg-emerald-50 border border-emerald-200 p-3 text-center">
                  <p className="text-2xl font-bold text-emerald-700">{result.created}</p>
                  <p className="text-xs text-emerald-700 font-semibold">Nowych</p>
                </div>
                <div className="rounded-md bg-blue-50 border border-blue-200 p-3 text-center">
                  <p className="text-2xl font-bold text-blue-700">{result.updated}</p>
                  <p className="text-xs text-blue-700 font-semibold">Zaktualizowanych</p>
                </div>
                <div className="rounded-md bg-red-50 border border-red-200 p-3 text-center">
                  <p className="text-2xl font-bold text-red-700">{result.errors?.length || 0}</p>
                  <p className="text-xs text-red-700 font-semibold">Błędów</p>
                </div>
              </div>

              {result.errors && result.errors.length > 0 && (
                <div className="rounded-md bg-red-50 border border-red-200 p-3 max-h-60 overflow-y-auto">
                  <p className="text-xs font-semibold uppercase tracking-wide text-red-700 mb-2 flex items-center gap-1"><AlertTriangle className="w-3.5 h-3.5" /> Wiersze z błędami</p>
                  <ul className="space-y-1 text-xs text-red-900">
                    {result.errors.map((er, i) => (
                      <li key={i} className="font-mono">
                        Wiersz {er.row} {er.sku || er.email || er.name ? `(${er.sku || er.email || er.name})` : ""}: {er.error}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {(result.created + result.updated > 0) && (!result.errors || result.errors.length === 0) && (
                <div className="rounded-md bg-emerald-50 border border-emerald-200 p-3 flex items-start gap-2">
                  <CheckCircle2 className="w-4 h-4 text-emerald-700 mt-0.5" />
                  <p className="text-xs text-emerald-900">Import zakończony bez błędów.</p>
                </div>
              )}

              <div className="flex justify-end gap-2 pt-2">
                <button onClick={() => { setResult(null); setFile(null); }} className="px-4 py-2 rounded-md border border-slate-300 text-sm font-semibold text-slate-700 hover:bg-slate-50">Importuj kolejny plik</button>
                <button onClick={onClose} className="px-4 py-2 rounded-md bg-[#FF6B1A] hover:bg-[#E65A10] text-white text-sm font-semibold">Zamknij</button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
